import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import Sidebar from "../components/Sidebar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { Badge } from "../components/ui/badge";
import { ScrollArea } from "../components/ui/scroll-area";
import {
  FileText,
  Plus,
  Loader2,
  Edit,
  Copy,
  Trash2,
  Upload,
  CheckCircle,
  GripVertical,
  X,
  Wand2,
  ArrowUp,
  ArrowDown,
  Columns,
  Search
} from "lucide-react";

const FIELD_TYPES = [
  { value: "text", label: "Texto" },
  { value: "number", label: "Número" },
  { value: "date", label: "Fecha" },
  { value: "textarea", label: "Texto largo" },
  { value: "select", label: "Lista de opciones" },
  { value: "checkbox", label: "Casilla de verificación" }
];

const COLUMN_WIDTHS = [
  { value: "full", label: "Ancho completo", icon: "▬▬▬▬" },
  { value: "half", label: "Mitad (1/2)", icon: "▬▬" },
  { value: "third", label: "Tercio (1/3)", icon: "▬" }
];

const SUGGESTED_FIELDS = [
  { name: "tipo_problema", label: "Tipo de Problema", field_type: "select", options: ["Hardware", "Software", "Red", "Otro"] },
  { name: "sistema_afectado", label: "Sistema Afectado", field_type: "text" },
  { name: "urgencia", label: "Nivel de Urgencia", field_type: "select", options: ["Baja", "Media", "Alta", "Crítica"] },
  { name: "fecha_inicio", label: "Fecha de Inicio del Problema", field_type: "date" },
  { name: "pasos_reproducir", label: "Pasos para Reproducir", field_type: "textarea" },
  { name: "resultado_esperado", label: "Resultado Esperado", field_type: "textarea" },
  { name: "resultado_actual", label: "Resultado Actual", field_type: "textarea" },
  { name: "capturas_pantalla", label: "¿Tiene capturas de pantalla?", field_type: "checkbox" },
  { name: "contacto_telefono", label: "Teléfono de Contacto", field_type: "text" },
  { name: "horario_disponible", label: "Horario Disponible", field_type: "text" },
  { name: "correo_contacto", label: "Correo de Contacto", field_type: "text" },
  { name: "departamento_solicitante", label: "Departamento Solicitante", field_type: "text" },
  { name: "canal_reporte", label: "Canal de Reporte", field_type: "select", options: ["Teléfono", "Correo", "Presencial", "WhatsApp", "Sistema"] },
  { name: "impacto", label: "Nivel de Impacto", field_type: "select", options: ["Bajo", "Medio", "Alto", "Crítico"] },
  { name: "frecuencia_problema", label: "Frecuencia del Problema", field_type: "select", options: ["Siempre", "Intermitente", "Ocurrió una vez"] },
  { name: "dispositivo", label: "Dispositivo", field_type: "select", options: ["PC", "Laptop", "Móvil", "Tablet", "Otro"] },
  { name: "navegador", label: "Navegador", field_type: "select", options: ["Chrome", "Edge", "Firefox", "Safari", "Otro"] },
  { name: "version_sistema", label: "Versión del Sistema", field_type: "text" },
  { name: "ramo_seguro", label: "Ramo / Tipo de Seguro", field_type: "select", options: ["Vida", "Salud", "Vehículos", "Incendio / Propiedad", "Fianzas", "Responsabilidad Civil", "Otros"] },
  { name: "numero_poliza", label: "Número de Póliza", field_type: "text" },
  { name: "cliente_asegurado", label: "Cliente / Asegurado", field_type: "text" },
  { name: "aseguradora", label: "Aseguradora", field_type: "text" },
  { name: "numero_factura", label: "Número de Factura", field_type: "text" },
  { name: "monto_involucrado", label: "Monto Involucrado", field_type: "number" },
  { name: "fecha_limite", label: "Fecha Límite Requerida", field_type: "date" },
  { name: "requiere_seguimiento", label: "¿Requiere seguimiento?", field_type: "checkbox" },
  { name: "afecta_otros_usuarios", label: "¿Afecta a otros usuarios?", field_type: "checkbox" },
  { name: "comentarios_adicionales", label: "Comentarios Adicionales", field_type: "textarea" }
];

export default function PlantillasTicketsPage({ modulo = null, embedded = false }) {
  const [plantillas, setPlantillas] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [showSuggestedDialog, setShowSuggestedDialog] = useState(false);
  const [editingPlantilla, setEditingPlantilla] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [subcatsDetectadas, setSubcatsDetectadas] = useState([]);
  const [formData, setFormData] = useState({
    nombre: "",
    contenido: "",
    categoria_id: "",
    subcategoria_activadora: "",
    fields: []
  });
  const [draggedIndex, setDraggedIndex] = useState(null);
  const fileInputRef = useRef(null);
  const { getAuthHeaders } = useAuth();
  // Cabeceras con módulo (Hallazgos Auditoria) cuando aplica
  const authH = () => {
    const base = (getAuthHeaders)();
    return modulo ? { ...base, headers: { ...base.headers, "X-Modulo": modulo } } : base;
  };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [plantillasRes, categoriasRes] = await Promise.all([
        axios.get(`${API}/plantillas-tickets`, authH()),
        axios.get(`${API}/tickets/categorias`, authH())
      ]);
      setPlantillas(plantillasRes.data);
      setCategorias(categoriasRes.data);
    } catch (err) {
      toast.error("Error al cargar datos");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const resetForm = () => {
    setFormData({
      nombre: "",
      contenido: "",
      categoria_id: "",
      subcategoria_activadora: "",
      fields: []
    });
    setSubcatsDetectadas([]);
    setEditingPlantilla(null);
  };

  const openNewDialog = () => {
    resetForm();
    setShowDialog(true);
  };

  const openEditDialog = (plantilla) => {
    setEditingPlantilla(plantilla);
    setFormData({
      nombre: plantilla.nombre,
      contenido: plantilla.contenido || "",
      categoria_id: plantilla.categoria_id || "",
      subcategoria_activadora: plantilla.subcategoria_activadora || "",
      fields: (plantilla.fields || []).map((f, idx) => ({
        ...f,
        column_width: f.column_width || "full",
        order: f.order ?? idx
      }))
    });
    setSubcatsDetectadas([]);
    // Cargar subcategorías detectadas de la categoría de la plantilla.
    if (plantilla.categoria_id) {
      axios.get(`${API}/tickets/subcategorias-reglas`, authH())
        .then(res => {
          const reglas = (res.data || []).filter(r => r.categoria_id === plantilla.categoria_id);
          setSubcatsDetectadas([...new Set(reglas.flatMap(r => r.subcategorias || []))]);
        })
        .catch(() => {});
    }
    setShowDialog(true);
  };

  const handleCategoriaChange = async (value) => {
    const catId = value === "none" ? "" : value;
    // Al cambiar de departamento, resetea la subcategoría activadora.
    setFormData(prev => ({
      ...prev,
      categoria_id: catId,
      subcategoria_activadora: ""
    }));
    setSubcatsDetectadas([]);
    if (!catId) return;
    try {
      // Detecta TODAS las subcategorías configuradas para esa "Departamento a
      // asignar" (todas las reglas), para ofrecerlas como posible activador.
      const res = await axios.get(`${API}/tickets/subcategorias-reglas`, authH());
      const reglas = (res.data || []).filter(r => r.categoria_id === catId);
      const subs = [...new Set(reglas.flatMap(r => r.subcategorias || []))];
      setSubcatsDetectadas(subs);
      if (subs.length > 0) {
        toast.success(`Se detectaron ${subs.length} subcategoría(s). Puedes elegir cuál activará estos campos.`);
      }
    } catch (e) {
      // silencioso
    }
  };

  const addField = () => {
    const newField = {
      id: `field_${Date.now()}`,
      name: "",
      label: "",
      field_type: "text",
      required: false,
      options: [],
      column_width: "full",
      order: formData.fields.length
    };
    setFormData(prev => ({
      ...prev,
      fields: [...prev.fields, newField]
    }));
  };

  const addSuggestedField = (suggestedField) => {
    const newField = {
      id: `field_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      name: suggestedField.name,
      label: suggestedField.label,
      field_type: suggestedField.field_type,
      required: false,
      options: suggestedField.options || [],
      column_width: "full",
      order: formData.fields.length
    };
    setFormData(prev => ({
      ...prev,
      fields: [...prev.fields, newField]
    }));
    toast.success(`Campo "${suggestedField.label}" agregado`);
  };

  const updateField = (index, key, value) => {
    setFormData(prev => ({
      ...prev,
      fields: prev.fields.map((f, i) => 
        i === index ? { ...f, [key]: value } : f
      )
    }));
  };

  const removeField = (index) => {
    setFormData(prev => ({
      ...prev,
      fields: prev.fields.filter((_, i) => i !== index)
    }));
  };

  const moveField = (index, direction) => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= formData.fields.length) return;
    
    const newFields = [...formData.fields];
    [newFields[index], newFields[newIndex]] = [newFields[newIndex], newFields[index]];
    newFields.forEach((f, idx) => f.order = idx);
    setFormData(prev => ({ ...prev, fields: newFields }));
  };

  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;
    
    const newFields = [...formData.fields];
    const draggedField = newFields[draggedIndex];
    newFields.splice(draggedIndex, 1);
    newFields.splice(index, 0, draggedField);
    newFields.forEach((f, idx) => f.order = idx);
    
    setFormData(prev => ({ ...prev, fields: newFields }));
    setDraggedIndex(index);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);

    try {
      const res = await axios.post(
        `${API}/plantillas-tickets/generate-from-file`, 
        formDataUpload,
        {
          ...authH(),
          headers: {
            ...authH().headers,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      const fieldsWithWidth = (res.data.fields || []).map((f, idx) => ({
        ...f,
        column_width: f.column_width || "full",
        order: idx
      }));

      setFormData(prev => ({
        ...prev,
        nombre: prev.nombre || res.data.suggested_name || "",
        contenido: prev.contenido || res.data.description || "",
        fields: [...prev.fields, ...fieldsWithWidth]
      }));
      
      toast.success(`Se extrajeron ${fieldsWithWidth.length} campos del documento`);
      setShowUploadDialog(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al procesar archivo");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.nombre.trim()) {
      toast.error("El nombre es requerido");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        nombre: formData.nombre,
        contenido: formData.contenido,
        categoria_id: formData.categoria_id || null,
        subcategoria_activadora: formData.subcategoria_activadora || null,
        fields: formData.fields.map((f, idx) => {
          const { auto_subcategoria, ...rest } = f;
          return { ...rest, order: idx };
        })
      };

      if (editingPlantilla) {
        await axios.put(`${API}/plantillas-tickets/${editingPlantilla.id}`, payload, authH());
        toast.success("Plantilla actualizada");
      } else {
        await axios.post(`${API}/plantillas-tickets`, payload, authH());
        toast.success("Plantilla creada");
      }
      setShowDialog(false);
      resetForm();
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const handleClone = (plantilla) => {
    // Abre el diálogo con los datos de la plantilla como NUEVA (sin id) para clonarla.
    setEditingPlantilla(null);
    setFormData({
      nombre: `${plantilla.nombre} (copia)`,
      contenido: plantilla.contenido || "",
      categoria_id: plantilla.categoria_id || "",
      subcategoria_activadora: plantilla.subcategoria_activadora || "",
      fields: (plantilla.fields || []).map((f, idx) => ({
        ...f,
        id: `field_${Date.now()}_${idx}`,
        column_width: f.column_width || "full",
        order: f.order ?? idx
      }))
    });
    setSubcatsDetectadas([]);
    if (plantilla.categoria_id) {
      axios.get(`${API}/tickets/subcategorias-reglas`, authH())
        .then(res => {
          const reglas = (res.data || []).filter(r => r.categoria_id === plantilla.categoria_id);
          setSubcatsDetectadas([...new Set(reglas.flatMap(r => r.subcategorias || []))]);
        })
        .catch(() => {});
    }
    setShowDialog(true);
    toast.info("Plantilla clonada. Ajusta el nombre y guarda para crear la copia.");
  };

  const handleDelete = async (plantillaId) => {
    if (!confirm("¿Eliminar esta plantilla?")) return;
    try {
      await axios.delete(`${API}/plantillas-tickets/${plantillaId}`, authH());
      toast.success("Plantilla eliminada");
      fetchData();
    } catch (err) {
      toast.error("Error al eliminar");
    }
  };

  const filteredPlantillas = plantillas.filter(p =>
    p.nombre.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (p.contenido && p.contenido.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  return (
    <div className={embedded ? "flex" : "flex min-h-screen bg-slate-50 dark:bg-slate-900"}>
      {!embedded && <Sidebar />}
      <main className={`flex-1 p-4 md:p-8 ${embedded ? "pt-4 md:pt-4" : "pt-14 md:pt-8 ml-0 md:ml-64"} overflow-x-hidden`}>
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 dark:text-white font-['Manrope']">
              Plantillas de Tickets
            </h1>
            <p className="text-slate-500 dark:text-slate-400 mt-1">
              Crea plantillas con campos personalizados para agilizar la creación de tickets
            </p>
          </div>
          <Button
            onClick={openNewDialog}
            className="bg-[#0C314C] hover:bg-[#2a4d7a] text-white"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nueva Plantilla
          </Button>
        </div>

        {/* Search */}
        <div className="mb-6">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
            <Input
              placeholder="Buscar plantillas..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        {/* Plantillas Grid */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Plantillas ({filteredPlantillas.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-[#0C314C]" />
              </div>
            ) : filteredPlantillas.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No hay plantillas creadas</p>
                <Button onClick={openNewDialog} variant="outline" className="mt-4">
                  Crear primera plantilla
                </Button>
              </div>
            ) : (
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {filteredPlantillas.map(plantilla => (
                  <Card key={plantilla.id} className="hover:shadow-md transition-shadow">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-2">
                        <h3 className="font-semibold text-slate-900 dark:text-white">
                          {plantilla.nombre}
                        </h3>
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEditDialog(plantilla)}
                            data-testid={`plantilla-edit-btn-${plantilla.id}`}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleClone(plantilla)}
                            title="Clonar plantilla"
                            data-testid={`plantilla-clone-btn-${plantilla.id}`}
                          >
                            <Copy className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(plantilla.id)}
                            className="text-red-500 hover:text-red-700"
                            data-testid={`plantilla-delete-btn-${plantilla.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                      {plantilla.categoria_nombre && (
                        <Badge variant="outline" className="mb-2">
                          {plantilla.categoria_nombre}
                        </Badge>
                      )}
                      <p className="text-sm text-slate-500 line-clamp-2 mb-2">
                        {plantilla.contenido || "Sin descripción"}
                      </p>
                      <div className="flex items-center gap-2 text-xs text-slate-400">
                        <span>{(plantilla.fields || []).length} campos</span>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Main Dialog */}
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col">
            <DialogHeader>
              <DialogTitle>
                {editingPlantilla ? "Editar Plantilla" : "Nueva Plantilla de Ticket"}
              </DialogTitle>
              <DialogDescription>
                Configura los campos que aparecerán en el formulario de nuevo ticket
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="flex-1 flex flex-col">
              <ScrollArea className="flex-1 pr-4">
                <div className="space-y-6 pb-4">
                  {/* Basic Info */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="nombre">Nombre *</Label>
                      <Input
                        id="nombre"
                        value={formData.nombre}
                        onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                        placeholder="Ej: Soporte Técnico General"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="categoria">Departamento a asignar</Label>
                      <Select
                        value={formData.categoria_id || "none"}
                        onValueChange={handleCategoriaChange}
                      >
                        <SelectTrigger data-testid="plantilla-categoria-select">
                          <SelectValue placeholder="Seleccionar departamento a asignar" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Sin asignar</SelectItem>
                          {categorias.map((cat) => (
                            <SelectItem key={cat.id} value={cat.id}>
                              {cat.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Activador de los campos: subcategoría o (por defecto) el Departamento a asignar */}
                  {formData.categoria_id && (
                    <div className="space-y-2 p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-900">
                      <Label htmlFor="subcat-activadora">Subcategoría que activa estos campos (opcional)</Label>
                      <Select
                        value={formData.subcategoria_activadora || "none"}
                        onValueChange={(value) => setFormData({ ...formData, subcategoria_activadora: value === "none" ? "" : value })}
                        disabled={subcatsDetectadas.length === 0}
                      >
                        <SelectTrigger data-testid="plantilla-subcategoria-activadora-select">
                          <SelectValue placeholder="Seleccionar subcategoría" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Ninguna — activar con el Departamento a asignar</SelectItem>
                          {subcatsDetectadas.map((sc) => (
                            <SelectItem key={sc} value={sc}>{sc}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-slate-500 dark:text-slate-400">
                        {subcatsDetectadas.length === 0
                          ? "Este Departamento a asignar no tiene subcategorías configuradas. Los campos se activarán al seleccionarlo."
                          : formData.subcategoria_activadora
                            ? `Los campos de esta plantilla aparecerán cuando el usuario elija la subcategoría "${formData.subcategoria_activadora}".`
                            : "Sin subcategoría seleccionada: los campos se activarán al elegir este Departamento a asignar."}
                      </p>
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label htmlFor="contenido">Descripción / Instrucciones</Label>
                    <Textarea
                      id="contenido"
                      value={formData.contenido}
                      onChange={(e) => setFormData({ ...formData, contenido: e.target.value })}
                      placeholder="Instrucciones para el usuario al llenar el formulario..."
                      rows={2}
                    />
                  </div>

                  {/* Fields Section */}
                  <div className="border-t pt-4">
                    <div className="flex items-center justify-between mb-4">
                      <Label className="text-base font-semibold">Campos del Formulario</Label>
                      <div className="flex gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => setShowSuggestedDialog(true)}
                        >
                          <Wand2 className="w-4 h-4 mr-1" />
                          Campos Sugeridos
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => setShowUploadDialog(true)}
                        >
                          <Upload className="w-4 h-4 mr-1" />
                          Desde Archivo
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={addField}
                        >
                          <Plus className="w-4 h-4 mr-1" />
                          Agregar Campo
                        </Button>
                      </div>
                    </div>

                    {formData.fields.length === 0 ? (
                      <div className="text-center py-8 text-slate-500 border-2 border-dashed rounded-lg">
                        <FileText className="w-8 h-8 mx-auto mb-2 opacity-50" />
                        <p>No hay campos agregados</p>
                        <p className="text-sm">Usa los botones de arriba para agregar campos</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {formData.fields.map((field, index) => (
                          <div
                            key={field.id}
                            draggable
                            onDragStart={(e) => handleDragStart(e, index)}
                            onDragOver={(e) => handleDragOver(e, index)}
                            onDragEnd={handleDragEnd}
                            className={`border rounded-lg p-4 bg-white dark:bg-slate-800 ${
                              draggedIndex === index ? 'opacity-50' : ''
                            }`}
                          >
                            <div className="flex items-start gap-3">
                              <div className="cursor-move text-slate-400 hover:text-slate-600 pt-2">
                                <GripVertical className="w-5 h-5" />
                              </div>
                              
                              <div className="flex-1 grid grid-cols-4 gap-3">
                                <div className="space-y-1">
                                  <Label className="text-xs">Etiqueta</Label>
                                  <Input
                                    value={field.label}
                                    onChange={(e) => updateField(index, 'label', e.target.value)}
                                    placeholder="Etiqueta del campo"
                                    className="h-9"
                                  />
                                </div>
                                <div className="space-y-1">
                                  <Label className="text-xs">Nombre interno</Label>
                                  <Input
                                    value={field.name}
                                    onChange={(e) => updateField(index, 'name', e.target.value.toLowerCase().replace(/\s/g, '_'))}
                                    placeholder="nombre_campo"
                                    className="h-9"
                                  />
                                </div>
                                <div className="space-y-1">
                                  <Label className="text-xs">Tipo</Label>
                                  <Select
                                    value={field.field_type}
                                    onValueChange={(value) => updateField(index, 'field_type', value)}
                                  >
                                    <SelectTrigger className="h-9">
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {FIELD_TYPES.map(type => (
                                        <SelectItem key={type.value} value={type.value}>
                                          {type.label}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </div>
                                <div className="space-y-1">
                                  <Label className="text-xs">Ancho</Label>
                                  <Select
                                    value={field.column_width || "full"}
                                    onValueChange={(value) => updateField(index, 'column_width', value)}
                                  >
                                    <SelectTrigger className="h-9">
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {COLUMN_WIDTHS.map(w => (
                                        <SelectItem key={w.value} value={w.value}>
                                          {w.icon} {w.label}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </div>
                              </div>

                              <div className="flex items-center gap-2 pt-6">
                                <div className="flex items-center gap-1">
                                  <Switch
                                    checked={field.required}
                                    onCheckedChange={(checked) => updateField(index, 'required', checked)}
                                  />
                                  <Label className="text-xs">Req.</Label>
                                </div>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => moveField(index, 'up')}
                                  disabled={index === 0}
                                >
                                  <ArrowUp className="w-4 h-4" />
                                </Button>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => moveField(index, 'down')}
                                  disabled={index === formData.fields.length - 1}
                                >
                                  <ArrowDown className="w-4 h-4" />
                                </Button>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => removeField(index)}
                                  className="text-red-500 hover:text-red-700"
                                >
                                  <X className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>

                            {field.field_type === 'select' && (
                              <div className="mt-3 ml-8">
                                <Label className="text-xs">Opciones (separadas por coma)</Label>
                                <Input
                                  value={(field.options || []).join(', ')}
                                  onChange={(e) => updateField(index, 'options', e.target.value.split(',').map(o => o.trim()).filter(Boolean))}
                                  placeholder="Opción 1, Opción 2, Opción 3"
                                  className="h-9 mt-1"
                                />
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </ScrollArea>

              <DialogFooter className="border-t pt-4 mt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowDialog(false)}
                  disabled={saving}
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  disabled={saving}
                  className="bg-[#0C314C] hover:bg-[#2a4d7a] text-white"
                >
                  {saving ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Guardando...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4 mr-2" />
                      {editingPlantilla ? "Guardar Cambios" : "Crear Plantilla"}
                    </>
                  )}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Upload Dialog */}
        <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Upload className="w-5 h-5" />
                Generar Campos desde Archivo
              </DialogTitle>
              <DialogDescription>
                Sube un documento (PDF, Word, Excel) y la IA extraerá los campos automáticamente
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <Input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.docx,.doc,.xlsx,.xls,.txt"
                onChange={handleFileUpload}
                disabled={uploading}
              />
              {uploading && (
                <div className="flex items-center gap-2 mt-4 text-blue-600">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Procesando documento con IA...</span>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowUploadDialog(false)} disabled={uploading}>
                Cancelar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Suggested Fields Dialog */}
        <Dialog open={showSuggestedDialog} onOpenChange={setShowSuggestedDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Wand2 className="w-5 h-5" />
                Campos Sugeridos
              </DialogTitle>
              <DialogDescription>
                Selecciona campos predefinidos para agregar a tu plantilla
              </DialogDescription>
            </DialogHeader>
            <ScrollArea className="max-h-96">
              <div className="space-y-2 pr-4">
                {SUGGESTED_FIELDS.map((field, idx) => (
                  <div
                    key={idx}
                    className="flex items-center justify-between p-3 border rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800"
                  >
                    <div>
                      <p className="font-medium">{field.label}</p>
                      <p className="text-sm text-slate-500">
                        Tipo: {FIELD_TYPES.find(t => t.value === field.field_type)?.label || field.field_type}
                        {field.options?.length > 0 && ` (${field.options.length} opciones)`}
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => addSuggestedField(field)}
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Agregar
                    </Button>
                  </div>
                ))}
              </div>
            </ScrollArea>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowSuggestedDialog(false)}>
                Cerrar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
